import {
  JiraFieldMappersKeys,
  type UIFieldInputType,
  type UiFieldValidationError,
  type JiraFieldMapper
} from './MapperInterface';
import type {
  DefaultValueOption,
  IssueTypeField,
  UIAllowedValue,
  UIFieldDefinition
} from '../prepareSendIssueFields';
import {
  IssueFieldSource,
  type MappingDefinition
} from './MappingSchemaModels';
import { defaultWithoutError } from './ShortTextMapper';

export class MultiOptionsMapper implements JiraFieldMapper {
  id = JiraFieldMappersKeys.MultiOptions;
  allowedCustomKeys = [
    'com.atlassian.jira.plugin.system.customfieldtypes:multiselect',
    'com.atlassian.jira.plugin.system.customfieldtypes:multicheckboxes'
  ];
  isEnum = true;

  canMapThisField(jiraSchema: IssueTypeField): boolean {
    const { type, items, custom } = jiraSchema.schema;
    const isArrayOfOptions = type === 'array' && items === 'option';
    const isValidCustomType =
      !!custom && this.allowedCustomKeys.includes(custom);

    return isArrayOfOptions && isValidCustomType;
  }

  getDisplayValue(
    fieldSchema: IssueTypeField,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    axeIssue: any,
    fieldMapping?: MappingDefinition
  ): {
    value?: string;
    uiFieldType: UIFieldInputType;
    allowedValues?: UIAllowedValue[];
  } {
    const uiAllowedValues = fieldSchema.allowedValues?.map(allowedValue => {
      return {
        id: allowedValue.id,
        name: allowedValue.value
      };
    });

    // If we have a mapping, use it and ignore all else
    if (
      fieldMapping &&
      fieldMapping.sourceType === IssueFieldSource.FixedValue
    ) {
      return {
        value: axeIssue[fieldMapping.source] as string,
        uiFieldType: 'multiselect',
        allowedValues: uiAllowedValues
      };
      // Of if there is a default value, use it
    } else if (
      fieldSchema.hasDefaultValue &&
      fieldSchema.defaultValue &&
      Array.isArray(fieldSchema.defaultValue) &&
      fieldSchema.defaultValue.length > 0
    ) {
      return {
        value: (fieldSchema.defaultValue as DefaultValueOption[])[0].id,
        uiFieldType: 'multiselect',
        allowedValues: uiAllowedValues
      };
    }

    return {
      uiFieldType: 'multiselect',
      allowedValues: uiAllowedValues
    };
  }

  convertDisplayToIntegrationValue(uiFieldDefinition: UIFieldDefinition): {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    integrationValue: any;
    error?: UiFieldValidationError;
  } {
    // Check for a case when there is no value provided
    if (!uiFieldDefinition.value || uiFieldDefinition.value === '') {
      if (uiFieldDefinition.rawSchema.required) {
        return {
          integrationValue: undefined,
          error: {
            ...defaultWithoutError,
            isMissingRequired: true
          }
        };
      } else {
        return {
          integrationValue: undefined,
          error: defaultWithoutError
        };
      }
    }

    // Find definition for selected value
    const selectedAllowedValue =
      uiFieldDefinition.rawSchema.allowedValues?.find(
        allowedValue => allowedValue.id === uiFieldDefinition.value
      );

    if (!selectedAllowedValue) {
      // If we could not find we will just tread as if no value was selected.
      // The other option is to treat as invalid value, but user will not be able to fix. So we do not want to do this.
      return {
        integrationValue: undefined,
        error: defaultWithoutError
      };
    }

    return {
      integrationValue: [selectedAllowedValue],
      error: defaultWithoutError
    };
  }
}
